import { CheckCircleFilled, UserAddOutlined } from '@ant-design/icons';
import { Checkbox, message } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { OrganizationUsersResponse, UserInvitation } from '../../api/organization';
import { USER_STATE_IN_ORGANIZATION } from '../../api/organization';
import { inviteUsersToOrganization } from '../../api/user';
import { LogoFullIcon } from '../../assets/CustomIcons/LogoFullIcon';
import UnionBg from '../../assets/icons/union-bg.svg';
import { OrganizationAccessRoles, useGetUserFromAccessToken } from '../../common/auth';
import { extractDomain, isCompanyEmail, isTemporaryEmail, isValidEmail } from '../../common/utils';
import { BaseButton } from '../../components/UI/BaseButton';
import CustomTooltip from '../../components/UI/Tooltip/CustomTooltip';
import TagInput, { EmailType, type TagInputRef } from '../../components/TagInput/TagInput';
import { useGetOrganizationInvites, useGetOrganizationUsers } from '../../serverState/organization';
import { useGetUsersWithWorkspaceAccess, useGetWorkspaceById } from '../../serverState/workspace';
import { UserInfoBlock } from './SpaceMembersModal/SpaceMembersModal';

export default function NewSpaceInvite() {
  const navigate = useNavigate();
  const { orgId, spaceId } = useParams();
  const currentUser = useGetUserFromAccessToken();
  const orgUsersQuery = useGetOrganizationUsers();
  const orgInvitesQuery = useGetOrganizationInvites();

  // Get workspace data by publicId from URL
  const workspaceQuery = useGetWorkspaceById(spaceId || null);

  // Get workspace users
  const workspaceUsersQuery = useGetUsersWithWorkspaceAccess(workspaceQuery.data?.id || null);

  const [usersToInvite, setUsersToInvite] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [inviteAll, setInviteAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const tagInputRef = useRef<TagInputRef>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showBottomShadow, setShowBottomShadow] = useState(false);

  // Combined type for org users and pending invites
  type SuggestionItem =
    | { type: 'user'; data: OrganizationUsersResponse }
    | { type: 'invite'; data: UserInvitation };

  // Filter and sort organization users and invites together
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);

  useEffect(() => {
    if (!orgUsersQuery.data || !workspaceUsersQuery.data || !orgInvitesQuery.data) {
      return;
    }

    // Filter org users
    const filteredUsers: SuggestionItem[] = orgUsersQuery.data
      .filter((orgUser) => {
        // Only show active members
        if (orgUser.usersStateInOrganization !== USER_STATE_IN_ORGANIZATION.ACTIVE) {
          return false;
        }

        // Filter out users already in the space
        const alreadyInSpace = workspaceUsersQuery.data.find((spaceUser) => spaceUser.User.id === orgUser.User.id);
        if (alreadyInSpace) {
          return false;
        }

        // Filter out users already added to invite list
        if (usersToInvite.includes(orgUser.User.email)) {
          return false;
        }

        // Apply search filter
        if (searchValue === '') {
          return true;
        }

        const user = orgUser.User;
        if (user.fullName?.toLowerCase().includes(searchValue.toLowerCase())) {
          return true;
        }
        if (user.email?.toLowerCase().includes(searchValue.toLowerCase())) {
          return true;
        }
        return false;
      })
      .map((orgUser) => ({ type: 'user' as const, data: orgUser }));

    // Filter invites
    const filteredInvites: SuggestionItem[] = orgInvitesQuery.data
      .filter((invite) => {
        // Filter out invites already added to invite list
        if (usersToInvite.includes(invite.email)) {
          return false;
        }

        if (searchValue === '') {
          return true;
        }
        if (invite.email?.toLowerCase().includes(searchValue.toLowerCase())) {
          return true;
        }
        return false;
      })
      .map((invite) => ({ type: 'invite' as const, data: invite }));

    // Combine and sort together
    const combined = [...filteredUsers, ...filteredInvites];

    const sorted = combined.sort((a, b) => {
      // Primary criteria: A → Z by name, if no name then email
      const getNameOrEmail = (item: SuggestionItem) => {
        if (item.type === 'user') {
          return (item.data.User.fullName || item.data.User.email || '').toLowerCase();
        }
        return (item.data.email || '').toLowerCase();
      };

      const nameA = getNameOrEmail(a);
      const nameB = getNameOrEmail(b);
      const nameComparison = nameA.localeCompare(nameB);

      if (nameComparison !== 0) {
        return nameComparison;
      }

      // Secondary criteria: Users who joined org first (earlier createdAt) on top
      const dateA = new Date(a.data.createdAt).getTime();
      const dateB = new Date(b.data.createdAt).getTime();
      return dateA - dateB;
    });

    setSuggestions(sorted);
  }, [orgUsersQuery.data, workspaceUsersQuery.data, orgInvitesQuery.data, searchValue, usersToInvite]);

  // Sync checkbox state with actual selection
  useEffect(() => {
    if (!orgUsersQuery.data || !orgInvitesQuery.data || !workspaceUsersQuery.data) {
      return;
    }

    // Get all available org users and invites (not filtered by search)
    const allOrgEmails =
      orgUsersQuery.data
        ?.filter((u) => u.usersStateInOrganization === USER_STATE_IN_ORGANIZATION.ACTIVE)
        .filter((u) => !workspaceUsersQuery.data?.find((s) => s.User.id === u.User.id))
        .map((u) => u.User.email) || [];
    const allInviteEmails = orgInvitesQuery.data?.map((i) => i.email) || [];
    const allAvailableEmails = [...allOrgEmails, ...allInviteEmails];

    if (allAvailableEmails.length === 0) {
      setInviteAll(false);
      return;
    }

    // Check if all available users are selected
    const allSelected = allAvailableEmails.every((email) => usersToInvite.includes(email));
    setInviteAll(allSelected);
  }, [orgUsersQuery.data, orgInvitesQuery.data, workspaceUsersQuery.data, usersToInvite]);

  // Check if scroll container has scrollable content and update shadow
  useEffect(() => {
    const checkScroll = () => {
      const container = scrollContainerRef.current;
      if (container) {
        const hasScroll = container.scrollHeight > container.clientHeight;
        const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 1;
        setShowBottomShadow(hasScroll && !isAtBottom);
      }
    };

    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScroll);
      }
      window.removeEventListener('resize', checkScroll);
    };
  }, [suggestions]);

  // Handle TagInput change with validation
  const handleChange = (values: string[]) => {
    if (values.length === 0) {
      setUsersToInvite([]);
      return;
    }

    // If values length is less than or equal to current, it's a deletion - just update without validation
    if (values.length <= usersToInvite.length) {
      setUsersToInvite([...values]);
      return;
    }

    const value = values.slice(-1)[0];

    if (value.length === 0) {
      return;
    } else if (!isValidEmail(value)) {
      void messageApi.error('Invalid email format. Try again.');
      return;
    } else if (isTemporaryEmail(value)) {
      void messageApi.error('Emails from temporary public domains are not allowed');
      return;
    }

    // Check if domain is allowed
    const isUserAdmin = orgAccess && orgAccess.roleToAssume === OrganizationAccessRoles.ADMIN;
    const hasCompanyEmail = currentUser.data?.user.email && isCompanyEmail(currentUser.data.user.email);
    const invitedDomain = extractDomain(value);
    const isSameDomain = domain === invitedDomain;

    // Org member with public email (gmail) should not be able to invite anyone
    if (!isUserAdmin && currentUser.data?.user.email && !hasCompanyEmail) {
      void messageApi.error('Only organization admins can invite users with other domain email addresses');
      return;
    }

    if (hasCompanyEmail && !isUserAdmin && !isSameDomain) {
      // Check if the invited domain is another org domain (used by other org members)
      const isOtherOrgDomain = orgUsersQuery.data?.some(
        (orgUser) => extractDomain(orgUser.User.email) === invitedDomain
      );

      if (isOtherOrgDomain) {
        void messageApi.error('Only organization admins can invite users with other domain email addresses');
      } else {
        void messageApi.error('Only organization admins can invite users with external domain email addresses');
      }
      return;
    }

    // Check if user is deactivated in the organization
    const orgUser = orgUsersQuery.data?.find((orgAccess) => orgAccess.User.email === value);
    if (orgUser && orgUser.usersStateInOrganization === USER_STATE_IN_ORGANIZATION.INACTIVE) {
      void messageApi.error('Can not invite deactivated users to the organization, they can be reactivated instead');
      setSearchValue('');
      return;
    }

    if (isAlreadyAdded(value)) {
      void messageApi.error('User with this email is already in the space.');
      setSearchValue('');
      return;
    } else {
      setUsersToInvite([...values]);
      setSearchValue('');
    }
  };

  const handleToggleUser = (email: string) => {
    if (usersToInvite.includes(email)) {
      setUsersToInvite(usersToInvite.filter((e) => e !== email));
    } else {
      setUsersToInvite([...usersToInvite, email]);
      // Clear the search input when adding a user from the suggestion list
      setSearchValue('');
    }
    // Focus the input after toggling
    setTimeout(() => {
      tagInputRef.current?.focus();
    }, 0);
  };

  const handleInviteAllChange = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    setInviteAll(checked);

    if (checked) {
      // Add ALL org users and invited users regardless of search filter
      const allOrgEmails =
        orgUsersQuery.data
          ?.filter((u) => u.usersStateInOrganization === USER_STATE_IN_ORGANIZATION.ACTIVE)
          .filter((u) => !workspaceUsersQuery.data?.find((s) => s.User.id === u.User.id))
          .map((u) => u.User.email) || [];
      const allInviteEmails = orgInvitesQuery.data?.map((i) => i.email) || [];
      const allEmails = Array.from(new Set([...usersToInvite, ...allOrgEmails, ...allInviteEmails]));
      setUsersToInvite(allEmails);
    } else {
      // Remove all org and invited users from usersToInvite, keep manually entered ones
      const allOrgEmails =
        orgUsersQuery.data
          ?.filter((u) => u.usersStateInOrganization === USER_STATE_IN_ORGANIZATION.ACTIVE)
          .map((u) => u.User.email) || [];
      const allInviteEmails = orgInvitesQuery.data?.map((i) => i.email) || [];
      const manualEmails = usersToInvite.filter(
        (email) => !allOrgEmails.includes(email) && !allInviteEmails.includes(email)
      );
      setUsersToInvite(manualEmails);

      // Focus the input field when unchecking
      setTimeout(() => {
        tagInputRef.current?.focus();
      }, 0);
    }
  };

  // Check if email domain is allowed
  const domain = extractDomain(currentUser.data?.user.email || '');
  const orgAccess = orgUsersQuery.data?.find((orgAccess) => orgAccess.userId === currentUser.data?.user.id);

  const isAllowedDomain = (email: string) => {
    if (currentUser.data?.user.email && !isCompanyEmail(currentUser.data.user.email)) {
      return true;
    }
    if (orgAccess && orgAccess.roleToAssume === OrganizationAccessRoles.ADMIN) {
      return true;
    }
    return domain === extractDomain(email);
  };

  const isAlreadyAdded = (email: string) => {
    const spaceAccess = workspaceUsersQuery.data?.find((spaceAccess) => spaceAccess.User.email === email);
    return !!spaceAccess;
  };

  const emailType = (email: string): EmailType => {
    if (!currentUser.data?.user.email) {
      return EmailType.sameDomain;
    }
    if (orgUsersQuery.data?.find((orgAccess) => orgAccess.User.email === email)) {
      return EmailType.orgUser;
    }
    if (isCompanyEmail(currentUser.data.user.email)) {
      return extractDomain(currentUser.data.user.email) === extractDomain(email)
        ? EmailType.sameDomain
        : EmailType.differentDomain;
    }

    return EmailType.differentDomain;
  };

  const handleInvite = async () => {
    // If there's text in the input, validate and add it first
    if (searchValue.trim()) {
      if (!isValidEmail(searchValue)) {
        void messageApi.error('Invalid email format. Try again.');
        tagInputRef.current?.focus();
        return;
      }
      if (isTemporaryEmail(searchValue)) {
        void messageApi.error('Emails from temporary public domains are not allowed');
        tagInputRef.current?.focus();
        return;
      }

      // Check if domain is allowed
      const isUserAdmin = orgAccess && orgAccess.roleToAssume === OrganizationAccessRoles.ADMIN;
      const hasCompanyEmail = currentUser.data?.user.email && isCompanyEmail(currentUser.data.user.email);
      const invitedDomain = extractDomain(searchValue);
      const isSameDomain = domain === invitedDomain;

      // Org member with public email (gmail) should not be able to invite anyone
      if (!isUserAdmin && currentUser.data?.user.email && !hasCompanyEmail) {
        void messageApi.error('Only organization admins can invite users with other domain email addresses');
        tagInputRef.current?.focus();
        return;
      }

      if (hasCompanyEmail && !isUserAdmin && !isSameDomain) {
        // Check if the invited domain is another org domain (used by other org members)
        const isOtherOrgDomain = orgUsersQuery.data?.some(
          (orgUser) => extractDomain(orgUser.User.email) === invitedDomain
        );

        if (isOtherOrgDomain) {
          void messageApi.error('Only organization admins can invite users with other domain email addresses');
        } else {
          void messageApi.error('Only organization admins can invite users with external domain email addresses');
        }
        tagInputRef.current?.focus();
        return;
      }

      // Check if user is deactivated in the organization
      const orgUser = orgUsersQuery.data?.find((orgAccess) => orgAccess.User.email === searchValue);
      if (orgUser && orgUser.usersStateInOrganization === USER_STATE_IN_ORGANIZATION.INACTIVE) {
        void messageApi.error('Can not invite deactivated users to the organization, they can be reactivated instead');
        tagInputRef.current?.focus();
        return;
      }

      if (isAlreadyAdded(searchValue)) {
        void messageApi.error('User with this email is already in the space.');
        tagInputRef.current?.focus();
        return;
      }
      // Add the email to the list if valid
      if (!usersToInvite.includes(searchValue)) {
        setUsersToInvite([...usersToInvite, searchValue]);
      }
      setSearchValue('');
    }

    // After validation, check if we have any users to invite
    const finalUsersToInvite =
      searchValue.trim() && isValidEmail(searchValue) && !usersToInvite.includes(searchValue)
        ? [...usersToInvite, searchValue]
        : usersToInvite;

    if (finalUsersToInvite.length === 0) {
      void messageApi.error('Please enter at least one valid email to invite.');
      tagInputRef.current?.focus();
      return;
    }

    setIsSubmitting(true);
    try {
      // Invite users to the organization with the space ID
      const workspaceId = workspaceQuery.data?.id;
      const users = finalUsersToInvite.map((email) => ({
        email,
        role: OrganizationAccessRoles.MEMBER,
      }));
      await inviteUsersToOrganization(users, workspaceId);

      // Refetch queries to update the UI with newly invited users
      await Promise.all([
        workspaceQuery.refetch(),
        orgUsersQuery.refetch(),
        workspaceUsersQuery.refetch(),
        orgInvitesQuery.refetch(),
      ]);

      void messageApi.success('Invitations sent successfully');

      // Navigate to the space backlog
      navigate(`/${orgId}/space/${spaceId}/backlog`);
    } catch (error: any) {
      const errorCode = error?.response?.data?.code;
      const errorMessage = error?.response?.data?.message;

      if (errorCode === 'ERR-USER-33') {
        void messageApi.error('Only admins can invite other admins');
      } else if (errorCode === 'ERR-USER-34') {
        void messageApi.error('Please provide valid user information');
      } else if (errorCode === 'ERR-USER-35') {
        void messageApi.error('Invalid role specified');
      } else if (errorMessage?.toLowerCase().includes('deactivated')) {
        void messageApi.error('Can not invite deactivated users to the organization, they can be reactivated instead');
      } else if (
        errorMessage?.toLowerCase().includes('external domain') ||
        errorMessage?.toLowerCase().includes('same non-public email domain') ||
        errorMessage?.toLowerCase().includes('other domain')
      ) {
        void messageApi.error('Only organization admins can invite users with external domain email addresses');
      } else {
        void messageApi.error(errorMessage || error?.response?.data?.error || 'Failed to invite users');
      }
      setIsSubmitting(false);
      tagInputRef.current?.focus();
    }
  };

  const handleSkip = () => {
    // Navigate to the space backlog
    navigate(`/${orgId}/space/${spaceId}/backlog`);
  };

  // Check if invite button should be disabled
  const isInviteDisabled = usersToInvite.length === 0 && !searchValue.trim();

  return (
    <div
      className="flex justify-center items-center min-h-screen bg-brand-white"
      style={{
        backgroundImage: `url(${UnionBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {contextHolder}
      <div className="flex flex-col w-[656px] h-[80vh] bg-brand-white border-2 border-solid border-taskonioGreen-600 rounded-[12px] py-6 px-10 relative shadow-[0_0_0_4px_#7ff5aa,0_4px_16px_0_rgba(112,119,146,0.4)] overflow-hidden">
        <div className="flex flex-col items-center justify-center gap-5 my-6">
          <LogoFullIcon />
          <div className="flex items-center justify-center gap-2 font-semibold text-base">
            <CheckCircleFilled className="text-taskonioGreen-600" />
            {workspaceQuery.data?.name || 'New Space'} successfully created
          </div>
        </div>

        <h1 className="font-semibold text-xl text-brand-grey-dark mb-4 text-left">
          Invite your teammates to Teamspace
        </h1>

        <div className="mb-4">
          <TagInput
            ref={tagInputRef}
            value={usersToInvite}
            emailType={emailType}
            onChange={handleChange}
            onTextContentChange={(e) => {
              setSearchValue(e);
            }}
            inputValue={searchValue}
            placeholder="Enter emails"
          />
        </div>

        <div
          ref={scrollContainerRef}
          className={`flex-1 overflow-y-auto min-h-0 relative ${showBottomShadow ? 'shadow-bottom' : ''}`}
          style={
            showBottomShadow
              ? {
                  boxShadow: 'inset 0 -8px 8px -8px rgba(0, 0, 0, 0.15)',
                }
              : {}
          }
        >
          {/* Checkbox row - always visible when there are org members available */}
          <div className="flex items-center justify-end mb-2 sticky top-0 bg-brand-white z-10 py-2">
            {suggestions.length > 0 && (
              <div className="text-neutral-900 text-xs font-semibold mr-auto">From Organization</div>
            )}
            <Checkbox checked={inviteAll} onChange={handleInviteAllChange} className="text-sm">
              Invite all Organization members
            </Checkbox>
          </div>
          {suggestions.length > 0 && (
            <div className="flex flex-col">
              {suggestions.map((item) =>
                item.type === 'user' ? (
                  <InvitedUserInfoRow
                    key={`user-${item.data.id}`}
                    access={item.data}
                    isSelected={usersToInvite.includes(item.data.User.email)}
                    onToggle={() => handleToggleUser(item.data.User.email)}
                  />
                ) : (
                  <InvitedUserInfoRow
                    key={`invite-${item.data.id}`}
                    invitation={item.data}
                    isSelected={usersToInvite.includes(item.data.email)}
                    onToggle={() => handleToggleUser(item.data.email)}
                  />
                )
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between gap-2 mt-2 pt-4 border-t border-neutral-300">
          <BaseButton type="secondary" size="large" onClick={handleSkip}>
            Skip for now
          </BaseButton>
          <BaseButton type="primary" size="large" onClick={handleInvite} disabled={isInviteDisabled || isSubmitting}>
            {isSubmitting ? 'Inviting...' : 'Invite to Teamspace'}
          </BaseButton>
        </div>
      </div>
    </div>
  );
}

// Unified User Info Row Component - Handles both users and invitations
const InvitedUserInfoRow = ({
  access,
  invitation,
  isSelected,
  onToggle,
}: {
  access?: Partial<OrganizationUsersResponse>;
  invitation?: Partial<UserInvitation>;
  isSelected: boolean;
  onToggle: () => void;
}) => {
  const user = access?.User;

  return (
    <div
      className="flex justify-between gap-2 items-center p-2 rounded cursor-pointer hover:bg-neutral-200 min-w-0 h-10"
      onClick={onToggle}
    >
      <div className="flex-1 min-w-0">
        <UserInfoBlock user={user} invitation={invitation} />
      </div>

      <CustomTooltip title={isSelected ? 'Remove from invite list' : 'Add to invite list'}>
        <UserAddOutlined
          style={{ color: isSelected ? 'var(--color-taskonioGreen-600)' : 'var(--color-neutral-900)', fontSize: 20 }}
          className="pr-3 flex-shrink-0"
        />
      </CustomTooltip>
    </div>
  );
};
